import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Interview, InterviewStage, InterviewStageName } from '../../types/interview';
import InterviewCard from './InterviewCard';
import InterviewNotesSidebar from './InterviewNotesSidebar';
import InterviewStageModal from './InterviewStageModal';
import { FiPlus } from 'react-icons/fi';

interface InterviewColumnProps {
  stageName: InterviewStageName;
  stage: InterviewStage;
  interview: Interview | null;
  onStageChange: (stageId: number, newStage: InterviewStageName) => void;
  onStageComplete: (stageId: number) => Promise<void>;
  onOpenNotes: (interview: Interview, stage: InterviewStage) => void;
  index: number;
  allStages: InterviewStageName[];
}

const InterviewColumn: React.FC<InterviewColumnProps> = ({
  stageName,
  stage,
  interview,
  onStageChange,
  onStageComplete,
  onOpenNotes,
  index,
  allStages
}) => {
  const stageTitle = stageName.charAt(0).toUpperCase() + stageName.slice(1);
  
  const stageColors: Record<InterviewStageName, string> = {
    screening: 'blue',
    technical: 'green',
    behavioral: 'yellow',
    final: 'purple',
    offer: 'pink',
    completed: 'gray'
  };
  
  const color = stageColors[stageName];

  return (
    <motion.div 
      className="w-80 flex-shrink-0" 
      data-stage={stageName}
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2, delay: index * 0.1 }}
    >
      <div className={`bg-${color}-50 rounded-lg p-4 h-full min-h-[200px]`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold text-${color}-900`}>
            {stageTitle}
          </h3>
          <span className={`px-2 py-1 text-sm font-medium text-${color}-700 bg-${color}-100 rounded-full`}>
            {interview ? 1 : 0}
          </span>
        </div>
        
        <div className="space-y-3">
          {interview ? (
            <InterviewCard
              key={interview.id}
              interview={interview}
              stage={stage}
              onStageChange={onStageChange}
              onStageComplete={onStageComplete}
              color={color}
              onOpenNotes={() => onOpenNotes(interview, stage)}
              allStages={allStages}
            />
          ) : (
            <motion.div 
              className="flex items-center justify-center h-32 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-lg"
              animate={{ opacity: 0.7 }}
              transition={{ duration: 0.2 }}
            >
              No interview in this stage
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const INTERVIEW_STAGES: InterviewStageName[] = ['screening', 'technical', 'behavioral', 'final', 'offer'];

export default InterviewColumn; 